import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import MainWrapper from '../wrappers/mainWrapper';
import { createPortal } from 'react-dom';

const ITEMS_PER_PAGE = 11;

const AllComparisons = () => {
  const [loadingStatus, setLoadingStatus] = useState('loading-spinner');
  const [comps, setComps] = useState([]);
  const [filteredComps, setFilteredComps] = useState([]);
  const [expandedRowIndex, setExpandedRowIndex] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [uniqueUsers, setUniqueUsers] = useState([]);
  const [userFilter, setUserFilter] = useState('');
  const [compIdFilter, setCompIdFilter] = useState('');
  const [errorPopup, setErrorPopup] = useState({ show: false, content: '', compId: '' });
  const [openMenu, setOpenMenu] = useState({ index: null, top: 0, left: 0 });

  // ----------------- FETCH COMPARISONS -----------------
  const getResults = useCallback(async (page = 1) => {
    try {
      setLoadingStatus('loading-spinner');
  
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', ITEMS_PER_PAGE);
  
      if (userFilter) params.append('user_email', userFilter);
      if (compIdFilter) params.append('compId', compIdFilter);
  
      const response = await fetch(`${process.env.REACT_APP_GET_ALL_COMPARISONS}?${params.toString()}`);
      const data = await response.json();
  
      setComps(data.results || []);
      setFilteredComps(data.results || []);
      setCurrentPage(data.currentPage || page); // update current page from backend
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error('Error fetching comparisons', err);
      setComps([]);
      setFilteredComps([]);
    } finally {
      setLoadingStatus('');
    }
  }, [userFilter, compIdFilter]);
  
  useEffect(() => {
    getResults();
  }, [getResults]);

  // ----------------- FETCH USERS -----------------
  const getAllUsers = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_GET_ALL_USERS}`);
      const data = await res.json();
      if (Array.isArray(data)) setUniqueUsers(data.sort((a, b) => a.email.localeCompare(b.email)));
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  }, []);

  useEffect(() => {
    getAllUsers();
  }, [getAllUsers]);

  // ----------------- FILTERING -----------------
  useEffect(() => {
    let filtered = [...comps];
    if (userFilter) filtered = filtered.filter(c => c.claimData?.user_email === userFilter);
    if (compIdFilter) filtered = filtered.filter(c => c._id.includes(compIdFilter));
    setFilteredComps(filtered);
  }, [comps, userFilter, compIdFilter]);

  // ----------------- UTILS -----------------
  const checkDate = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    if (isNaN(d)) return '-';
    return `${String(d.getUTCMonth() + 1).padStart(2, '0')}/${String(d.getUTCDate()).padStart(2, '0')}/${d.getUTCFullYear()}`;
  };

  const changeStatusOutput = (item) => {
    switch (item.status) {
      case 'Finished': return 'Finished';
      case 'Pending': return 'Pending';
      case 'Failed': return 'This one stumped me.';
      case 'Archived': return 'Archived';
      default: return '-';
    }
  };

  const getMessage = (item) => item.statusMSG || '';

  const handlePdfClick = async (compId, fileName) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_GET_PDFS}/${compId}/${encodeURIComponent(fileName)}`);
      const data = await res.json();
      if (data.url) window.open(data.url, '_blank');
    } catch (err) {
      console.error('Error fetching PDF URL', err);
    }
  };

  const extractProgress = (message) => {
    const match = message.match(/(\d+)%/);
    return match ? parseInt(match[1], 10) : 0;
  };

  const friendlyErrorMessage = (rawMsg) => {
    if (!rawMsg) return 'Support has been notified of the issue.';
    const msg = rawMsg.toLowerCase();
    if (msg.includes('no line items found')) return 'Please ensure both documents have enumerated line items.';
    if (msg.includes('invalid est_headers_list') || msg.includes('invalid ins_headers_list')) return 'Make sure your documents are properly formatted - Preferrably, a Final Draft Xactimate.';
    if (msg.includes('invalid or corrupt pdf file')) return 'Please make sure your PDF is not corrupted.';
    return 'An unexpected issue occurred.';
  };

  const copyComparisonId = (compId) => {
    navigator.clipboard.writeText(compId);
    alert('Comparison ID copied!');
  };

  const toggleMenu = (e, index) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (openMenu.index === index) setOpenMenu({ index: null, top: 0, left: 0 });
    else setOpenMenu({ index, top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
  };

  const renderActionsMenu = (item, index) => (
    <div className="meatball-container">
      <button className="meatball-button" onClick={(e) => toggleMenu(e, index)}>⋮</button>
    </div>
  );

  // ----------------- TABLE -----------------
  const renderComparisonsTable = () => {
    if (loadingStatus) return <div className="loading">Loading...</div>;

    return (
      <div>
        {/* FILTERS */}
        <div style={{ marginBottom: '16px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <div>
            <label>User Filter: </label>
            <select value={userFilter} onChange={(e) => setUserFilter(e.target.value)}>
              <option value="">All Users</option>
              {uniqueUsers.map(u => <option key={u._id} value={u.email}>{u.email}</option>)}
            </select>
          </div>
          <div>
            <label>Comparison ID: </label>
            <input type="text" placeholder="Enter Comparison ID" value={compIdFilter} onChange={(e) => setCompIdFilter(e.target.value)} />
          </div>
        </div>

        {filteredComps.length === 0 ? (
          <p>No comparisons match your filter.</p>
        ) : (
          <>
            <table className="comparisons-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Adjuster</th>
                  <th>Insured</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Message</th>
                  <th>PDFs</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredComps.map((item, idx) => {
                  const rowIndex = idx;
                  return (
                    <React.Fragment key={item._id}>
                      <tr className="row-wrapper">
                        <td>{item.claimData?.user_email}</td>
                        <td>{item.claimData.adjuster}</td>
                        <td>{item.claimData.insured}</td>
                        <td>{checkDate(item.dateUploaded)}</td>
                        <td>{changeStatusOutput(item)}</td>
                        <td>
                          {item.status === 'Failed' ? (
                            <div style={{ cursor: 'pointer' }}
                                 onClick={() => setErrorPopup({ show: true, content: friendlyErrorMessage(getMessage(item)), compId: item._id })}>
                              ℹ️ Click for details
                            </div>
                          ) : getMessage(item).includes('%') ? (
                            <div className="progress-wrapper">
                              <span>{getMessage(item)}</span>
                              <div className="progress-container">
                                <div className="progress-bar" style={{ width: extractProgress(getMessage(item)) + '%' }}></div>
                              </div>
                            </div>
                          ) : (
                            <span>{getMessage(item)}</span>
                          )}
                        </td>
                        <td>
                          {[0,1].map(i => (
                            <button
                              key={i}
                              onClick={() => handlePdfClick(item.claimData.user_id, item.files[i])}
                              disabled={!item.files[i]}
                              style={{ marginRight: '6px', cursor: item.files[i] ? 'pointer' : 'not-allowed' }}
                            >
                              PDF {i + 1}
                            </button>
                          ))}
                        </td>
                        <td>{renderActionsMenu(item, rowIndex)}</td>
                      </tr>

                      <tr className={expandedRowIndex === rowIndex ? 'details-visible' : 'details-hidden'}>
                        <td colSpan="8" className="details-cell">
                          {expandedRowIndex === rowIndex && (
                            <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(item, null, 2)}</pre>
                          )}
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>

            {/* PAGINATION */}
            <div className="pagination-container">
              <button disabled={currentPage === 1} onClick={() => getResults(currentPage - 1)}>Previous</button>
              <span>Page {currentPage} of {totalPages}</span>
              <button disabled={currentPage === totalPages} onClick={() => getResults(currentPage + 1)}>Next</button>
            </div>
          </>
        )}

        {/* ERROR POPUP */}
        {errorPopup.show && createPortal(
          <div className="error-popup">
            <div>{errorPopup.content}</div>
            <button onClick={() => copyComparisonId(errorPopup.compId)}>Copy Comparison ID</button>
            <button onClick={() => setErrorPopup({ show: false, content: '', compId: '' })}>Close</button>
          </div>,
          document.body
        )}
      </div>
    );
  };

  return (
    <div className="outer-container">
      <MainWrapper />
      <div className="right-container">
        <div className="create-new-claim-container">
          <h1>All Comparisons</h1>
          <div className="all-comparisons-table">{renderComparisonsTable()}</div>
        </div>
      </div>

      {/* MEATBALL MENU */}
      {openMenu.index !== null && createPortal(
        <div className="meatball-menu-floating" style={{ top: openMenu.top, left: openMenu.left }}>
            {/* Only show VIEW RESULTS if the comparison is Finished */}
            {filteredComps[openMenu.index]?.status === 'Finished' && (
            <button>
                <NavLink
                to={filteredComps[openMenu.index]?.cleanParsedEstData?.detectedZones
                    ? `/restructuredResults/${filteredComps[openMenu.index]._id}`
                    : `/comparisonResults/${filteredComps[openMenu.index]._id}`}
                style={{ color: 'white', textDecoration: 'none' }}
                >
                VIEW RESULTS
                </NavLink>
            </button>
            )}

            <button onClick={() => setExpandedRowIndex(expandedRowIndex === openMenu.index ? null : openMenu.index)}>
            {expandedRowIndex === openMenu.index ? 'HIDE' : 'SHOW'} DETAILS
            </button>
        </div>,
        document.body
        )}


      <style>{`
        .row-wrapper { position: relative; }
        .details-hidden { display: none; }
        .details-visible { display: table-row; background-color: #f9f9f9; }
        .details-cell { padding: 12px; border-top: 1px solid #ccc; }
        .comparisons-table { width: 100%; border-collapse: collapse; }
        .comparisons-table th, .comparisons-table td { padding: 8px; border: 1px solid #ddd; text-align: left; }
        .pagination-container { display: flex; justify-content: center; align-items: center; margin-top: 1rem; gap: 10px; }
        .pagination-container button { background-color: #00B3B5; color: white; border: none; padding: 6px 12px; border-radius: 6px; cursor: pointer; min-width: 200px; }
        .pagination-container button:disabled { opacity: 0.5; cursor: not-allowed; }
        .progress-wrapper { display: flex; flex-direction: column; gap: 4px; }
        .progress-container { width: 100%; background-color: #eee; border-radius: 6px; }
        .progress-bar { height: 12px; background-color: #00B3B5; border-radius: 6px; }
        .meatball-container { position: relative; display: inline-block; }
        .meatball-button { background: none; border: none; cursor: pointer; font-size: 20px; }
        .meatball-menu-floating { position: fixed; background: #00B3B5; border: 1px solid #ccc; box-shadow: 0 2px 8px rgba(0,0,0,0.2); z-index: 9999; display: flex; flex-direction: column; padding: 4px 0; min-width: 140px; }
        .meatball-menu-floating button { border: none; background: none; padding: 8px 16px; cursor: pointer; text-align: left; width: 100%; color: white; }
        .error-popup { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); background-color: #00B3B5; color: white; padding: 16px 24px; border-radius: 8px; z-index: 10001; max-width: 400px; text-align: center; display: flex; flex-direction: column; gap: 10px; box-shadow: 0 4px 12px rgba(0,0,0,0.3); }
        .error-popup button { background: white; color: #00B3B5; border: none; padding: 6px 12px; border-radius: 4px; cursor: pointer; font-weight: bold; }
      `}</style>
    </div>
  );
};

export default AllComparisons;
